import { UrlHelper } from '../utils/UrlHelper';

// Instances can have tags that describe additional remediation information
export enum RemediationTag {
  WEBLOGIC_IDENTITY_KEYSTORE = 'weblogic-identity-keystore',
  PRIMEFACES_STRINGENCRYPTER = 'primefaces-stringencrypter',
  RSA_SMALL_GENERATED = 'rsa-small-generated',
  JCEKS_KEYSTORE = 'jkecs-keystore',
  TLS_MD5_SHA1 = 'tls-md5-sha1',
  DEFAULT_TRUSTSTORE_PASSWORD = 'default-truststore-password',
  JKS_KEYSTORE = 'jks-keystore',
  BKS_V1_KEYSTORE = 'bks-v1-keystore',
  S3_ENCRYPTION_CLIENT_ECB = 's3-encryption-client-ecb',
  S3_ENCRYPTION_CLIENT_CBC = 's3-encryption-client-cbc',
}

export const TAG_BASED_REMEDIATIONS = (): { [key in RemediationTag]: string } => {
  const keystoresUrl = UrlHelper.wrapKnowledgeBaseUrl('/knowledge-base/java-keystores');
  const keylengthsUrl = UrlHelper.wrapKnowledgeBaseUrl('/knowledge-base/cryptographic-keylengths');
  const pbeUrl = UrlHelper.wrapKnowledgeBaseUrl('/knowledge-base/password-based-key-derivation');

  return {
    [RemediationTag.WEBLOGIC_IDENTITY_KEYSTORE]: `
In this instance, the keystore appears to be an "Identity Keystore" in the Oracle
WebLogic framework. In this case, keystore type can be configured from the
<italic>Admin Console → Keystore tab → Keystore Type</italic> field.
Select JCEKS as the keystore type if it is available. The JKS keystore can be
converted to a JCEKS one using the Keytool.
<br/>
<a target="_blank" href="http://docs.oracle.com/cd/E13222_01/wls/docs103/ConsoleHelp/taskhelp/security/ConfigureKeystoresAndSSL.html">WebLogic documentation on keystores</a>
`,
    [RemediationTag.PRIMEFACES_STRINGENCRYPTER]: `
This password-based key derivation happens in the PrimeFaces library. This is a known
issue discovered by Cryptosense. The library should be upgraded to version
6.0.6.
`,
    [RemediationTag.RSA_SMALL_GENERATED]: `
This RSA key has been generated by the application. Key length is a parameter to the
<code>GenerateKeyPair()</code> method.
<br/>
<a target="_blank" href="${keylengthsUrl}">Knowledge Base Article on Key Lengths</a>
`,
    [RemediationTag.JCEKS_KEYSTORE]: `
The JCEKS keystore uses several weak hardcoded parameters that are fixed by
the provider.
Either the keystore should be replaced by PKCS12, or password policy should
be audited to ensure security even with this weak PBKDF.
<br/>
<a target="_blank" href="${keystoresUrl}">Knowledge Base article about key stores</a>
<br/>
<a target="_blank" href="${pbeUrl}">Knowledge Base article about password-based key derivation</a>
`,
    [RemediationTag.TLS_MD5_SHA1]: `
Versions of TLS before 1.2 use a combination of MD5 and SHA1 to authenticate the handshake messages.
<br/>
This can be fixed by using TLS 1.2 or above.
`,
    [RemediationTag.DEFAULT_TRUSTSTORE_PASSWORD]: `
<code>"changeit"</code> is the default password for system keystore. This
corresponds to a file named <code>cacerts</code> either in a <code>/etc</code>
subdirectory or in <code>$JAVA_HOME/lib/security</code>. The password can be
changed using the following command:
<br/>
<code>keytool -storepasswd -keystore /path/to/cacerts</code>
`,
    [RemediationTag.JKS_KEYSTORE]: `
The JKS keystore should be changed for a more secure type. JCEKS is a more
secure drop-in replacement in the Oracle crypto provider, using 3DES
encryption for the private keys. Care should be taken with JCEKS to use a long
passphrase, since password-based key derivation uses only 20 iterations.
<br/>
PKCS#12 keystores use 3DES encryption for private keys and 1024 iterations in
their key derivation, which is more secure. However, there is just one
passphrase for the whole keystore rather than one passphrase per key as for
JKS and JCEKS, hence more code changes may be required.
<br/>
Alternatively, the BCFKS keystore from the Bouncy Castle provider is a modern
keystore design with no known weaknesses.
<br/>
<a target="_blank" href="${keystoresUrl}">Knowledge Base article about key stores</a>
<br/>
<a target="_blank" href="/knowledge-base/password-based-key-derivation">Knowledge Base article about password-based key derivation</a>
`,
    [RemediationTag.BKS_V1_KEYSTORE]: `
The BKS-V1 keystore (called BKS in Bouncy Castle versions < 1.47) contains a
design flaw where the integrity MAC is only 16 bits, meaning that a valid
integrity password can be brute-forced in less than 10 seconds. This keystore
type should be replaced by a more secure type, either PKCS12, or preferably
BCFKS, the Bouncy Castle FIPS keystore, which has no known weaknesses.
<br/>
<a target="_blank" href="${keystoresUrl}">Knowledge Base article about key stores</a>
<br/>
<a target="_blank" href="${pbeUrl}">Knowledge Base article about password-based key derivation</a>
`,
    [RemediationTag.S3_ENCRYPTION_CLIENT_ECB]: `
By default, the <code>AmazonS3EncryptionClient</code> class that is used to perform
client-side encryption of objects uploaded to AWS S3 uses AES-ECB mode to wrap the data keys
that are used to encrypt the individual uploaded objects.
<br/>
This key-wrapping method is now marked as "not recommended" in the AWS cryptography
SDK, but cannot be set explicitly. To use a more secure method, you can either
<bold>use a KMS-managed key</bold> or <bold>switch the object encryption to use authenticated encryption</bold> (this triggers the use of AES Keywrap/RSA-OAEP for wrapping of all data keys).
<br/>
<a target="_blank" href="https://aws.amazon.com/blogs/developer/amazon-s3-client-side-authenticated-encryption/">AWS blog detailing how to enable authenticated encryption</a>
<br/>
<a target="_blank" href="https://docs.aws.amazon.com/general/latest/gr/aws_sdk_cryptography.html">AWS SDK cryptography documentation</a>
`,
    [RemediationTag.S3_ENCRYPTION_CLIENT_CBC]: `
By default, the <code>AmazonS3EncryptionClient</code> class that is used to perform
client-side encryption of objects uploaded to AWS S3 uses AES-CBC mode to encrypt the uploaded
objects.
<br/>
This encryption method is not recommended, as it does not authenticated the value of the
ciphertexts. To use an authenticated encryption mode (AES-GCM), configure your client with
a <code>CryptoConfiguration</code> object that has <code>CryptoMode.AuthenticatedEncryption</code> set.
<br/>
<a target="_blank" href="https://aws.amazon.com/blogs/developer/amazon-s3-client-side-authenticated-encryption/">AWS blog detailing how to enable authenticated encryption</a>
<br/>
<a target="_blank" href="https://docs.aws.amazon.com/general/latest/gr/aws_sdk_cryptography.html">AWS SDK cryptography documentation</a>
`,
  };
};
